import { AfterViewInit, Component, inject, input, Input, ViewChild } from '@angular/core';

import { LinesEditorComponent } from '../../../../../components';
import { ColumnDefinitionsService } from '../../../../../services/column-definitions.service';
import {
  CellValueChangedEvent,
  ColDef,
  FirstDataRenderedEvent,
  IRowNode,
} from '@ag-grid-community/core';
import { CurrentCompanyStore } from '@digiwall/goliath-core';
import { ArticleRangeValueStore } from '@digiwall/goliath-products';
import { CreateDeliveryNoteLineDto } from '@digiwall/goliath-documents/proxy';
import { FieldTypes } from '@digiwall/ag-grid';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';

@Component({
  selector: 'document-delivery-note-deposit-grid',
  templateUrl: './delivery-note-deposit-grid.component.html',
  imports: [LinesEditorComponent, LocalizationModule],
})
export class DeliveryNoteDepositGridComponent implements AfterViewInit {
  protected readonly currentCompanyStore = inject(CurrentCompanyStore);
  protected readonly columnDefinitionsService = inject(ColumnDefinitionsService);
  protected readonly articleRangeValueStore = inject(ArticleRangeValueStore);

  @Input() set lines(value: any[]) {
    this._lines = value?.filter(x => x.isDeposit);
    this.processLines();
  }

  _lines: CreateDeliveryNoteLineDto[];
  public get lines() {
    return this._lines;
  }

  salesPurchaseId = input<string>(null);
  allowPartialDelivery = input<boolean>(false);

  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  protected columnDefs: ColDef[] = [
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
    },
    {
      field: 'remainingAmountToDeliver',
      headerName: this.localizationService.instant('Doc::DeliveryNote:Amount'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
    },
    {
      field: 'amountToDeliver',
      headerName: this.localizationService.instant('Doc::DeliveryNote:ToDeduce'),
      editable: params =>
        this.allowPartialDelivery() &&
        params.node.isSelected() &&
        params.data.allowPartialDelivery &&
        params.data.remainingAmountToDeliver > 0,
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
    },
    {
      field: 'balanceToDeliver',
      headerName: this.localizationService.instant('Doc::BalanceDue'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
  ];

  constructor(
    private localizationService: LocalizationService,
    private toasterService: ToasterService,
  ) {}

  ngAfterViewInit(): void {
    this.lineGrid.grid.grid.api.setGridOption('rowSelection', {
      mode: 'multiRow',
      isRowSelectable: this.isRowSelectable,
      enableSelectionWithoutKeys: true,
      checkboxes: params => {
        if (!this.allowPartialDelivery()) return false;
        if (params.data.hasAlreadyBeenDelivered) return false;
        if (params.data.backOrders + params.data.reservation > 0) return false;
        return params.data.remainingQuantityToDeliver != 0 && params.data.cancelledQuantity == 0;
      },
      hideDisabledCheckboxes: true,
    });

    this.lineGrid.grid.grid.api.setGridOption('selectionColumnDef', {
      pinned: 'left',
      lockPinned: true,
    });

    this.lineGrid.grid.grid.api.setGridOption('onRowSelected', event => {
      if (event.data == null) return;

      let line = event.data;

      line.selectedQuantityToDeliver = event.node.isSelected()
        ? line.remainingQuantityToDeliver
        : null;

      if (event.node.isSelected() && event.node.parent != null) {
        event.node.parent.setSelected(true);
      }

      this.lineGrid.grid.grid.api.applyTransaction({ update: [line] });
    });

    this.lineGrid.grid.grid.api.setGridOption(
      'onFirstDataRendered',
      (params: FirstDataRenderedEvent<any>) => {
        const nodesToSelect: IRowNode[] = [];
        params.api.forEachNode(node => {
          if (node.data.remainingQuantityToDeliver != 0 && !node.data.hasAlreadyBeenDelivered) {
            nodesToSelect.push(node);
          }
        });
        params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
      },
    );
  }

  getDataPath = (line, paths: string[] = []) => {
    if (line == null) return paths;

    if (line.lineFromId != null) {
      paths = this.getDataPath(
        this.lines.find(x => x.id == line.lineFromId),
        paths,
      );
    }
    paths.push(line.id);
    return paths;
  };

  getContextMenuItems = _ => [];

  public isRowSelectable = (): boolean => {
    return this.allowPartialDelivery();
  };

  protected onCellValueChanged = (event: CellValueChangedEvent<any, any>) => {
    if (event.column.getColId() == 'amountToDeliver') {
      this.onToDeduceAmountChanged(event.data);
      if (
        event.data.amountToDeliver > event.data.remainingAmountToDeliver &&
        !event.data.allowOrderedQuantityOverrun
      ) {
        this.toasterService.error('Doc::DeliveryNote:ExceededQuantityForDelivery');
        event.data.selectedQuantityToDeliver = event.data.remainingQuantityToDeliver;
        event.data.amountToDeliver = event.data.remainingAmountToDeliver;
      }

      if (event.data.selectedQuantityToDeliver == 0) event.node.setSelected(false);

      this.lineGrid.grid.grid.api.applyTransaction({ update: [event.data] });
    }
  };

  private processLines(): void {
    if (!this._lines?.length) return;

    this.lineGrid.grid.grid.api.autoSizeAllColumns();
  }

  private onToDeduceAmountChanged(line: any): void {
    line.balanceToDeliver = line.remainingAmountToDeliver - line.amountToDeliver;
    line.selectedQuantityToDeliver =
      line.remainingQuantityToDeliver -
      (line.balanceToDeliver / line.remainingAmountToDeliver) * line.remainingQuantityToDeliver;

    this.lineGrid.grid.grid.api.redrawRows();
  }

  protected rowClassRules = {
    'footer-row': params => params.node.data.bottomRow,
    'disabled-row': params => params.node.data.remainingQuantityToDeliver == 0,
  };
}
